/**
 * CreateSparkPanel.jsx  — v2
 * Wide-screen optimised wizard layout with prominent step navigation.
 * No changes to ChatPanel.jsx wiring required — props are identical to v1.
 */

import React, { useState, useCallback } from "react";
import UsageLogService from "../services/UsageLogService";
import SparkStoreModelsService from "../services/SparkStoreModelsService";
import ApiClient from "../services/ApiClient";
import AuthTokenProvider from "../services/AuthTokenProvider";

// ─── Constants ──────────────────────────────────────────────────────────────

const FALLBACK_SPARK_MODELS = [
    { value: "openAI-o3",            label: "openAI-o3",            desc: "Thinks deeply — best for complex reasoning" },
    { value: "openAI-o3-mini",       label: "openAI-o3-mini",       desc: "Thinks fast — lightweight reasoning" },
    { value: "openAI-4o-2024-11-20", label: "openAI-4o-2024-11-20", desc: "Updated version, general tasks" },
    { value: "openAI-4.1",           label: "openAI-4.1",           desc: "Handles most tasks reliably" },
    { value: "openAI-4.1-mini",      label: "openAI-4.1-mini",      desc: "Efficient coding help" },
    { value: "openAI-4.1-nano",      label: "openAI-4.1-nano",      desc: "Rapid simple responses" },
    { value: "openAI-5",             label: "openAI-5",             desc: "Smart problem solver" },
    { value: "openAI-5.1",           label: "openAI-5.1",           desc: "Daily work, writing, coding" },
];

const FALLBACK_SPARK_STYLES = [
    { value: "Balanced",  label: "Balanced",  desc: "Mix of creativity and accuracy" },
    { value: "Creative",  label: "Creative",  desc: "More imaginative and exploratory" },
    { value: "Precise",   label: "Precise",   desc: "Factual and conservative" },
    { value: "Concise",   label: "Concise",   desc: "Short, direct answers" },
    { value: "Detailed",  label: "Detailed",  desc: "Long, thorough explanations" },
];

const FALLBACK_RESPONSE_LENGTHS = [
    { value: "Short",  label: "Short",  desc: "Brief, to-the-point responses" },
    { value: "Medium", label: "Medium", desc: "Balanced detail — recommended default" },
    { value: "Long",   label: "Long",   desc: "Thorough, in-depth explanations" },
];

const STEPS = [
    { key: "basic",     num: 1, label: "Details",       desc: "Name, owner and description" },
    { key: "params",    num: 2, label: "Configuration",  desc: "Model, style and behaviour" },
    { key: "discovery", num: 3, label: "Discovery",      desc: "Tags and sample questions" },
];

const INITIAL_FORM = {
    name: "", description: "",
    model: "openAI-4.1", style: "Balanced", length: "Medium",
    maxTokens: 600, temperature: 0.2,
    customInstruction: "", tags: "", sampleQuestions: "",
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const FieldLabel = ({ children, required, hint, charCount, maxChars, textSecondary, textTertiary }) => (
    <label style={{
        fontSize: 13, fontWeight: 600,
        display: "flex", alignItems: "baseline", gap: 6,
        marginBottom: 8, flexWrap: "wrap", color: textSecondary,
    }}>
        {children}
        {required && <span style={{ color: "#ef4444" }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: textTertiary, fontSize: 12 }}>{hint}</span>}
        {maxChars != null && (
            <span style={{
                marginLeft: "auto", fontWeight: 700, fontSize: 11,
                color: charCount >= maxChars ? "#ef4444" : charCount > maxChars * 0.85 ? "#f59e0b" : textTertiary,
            }}>
                {charCount} / {maxChars}
            </span>
        )}
    </label>
);

const StyledSelect = ({ value, onChange, options, isDark, textColor }) => (
    <div style={{ position: "relative" }}>
        <select value={value} onChange={e => onChange(e.target.value)}
            style={{
                width: "100%", padding: "11px 36px 11px 14px", borderRadius: 10, fontSize: 14,
                border: isDark ? "1.5px solid rgba(255,255,255,0.14)" : "1.5px solid rgba(0,0,0,0.14)",
                background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
                color: textColor, outline: "none", appearance: "none", cursor: "pointer", fontWeight: 500,
            }}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{
            position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)",
            pointerEvents: "none", fontSize: 11,
            color: isDark ? "rgba(255,255,255,0.40)" : "rgba(0,0,0,0.35)",
        }}>▾</div>
    </div>
);

const ReadOnlyField = ({ value, icon, isDark, textSecondary, textTertiary }) => (
    <div style={{
        padding: "11px 14px", borderRadius: 10, fontSize: 14,
        border: isDark ? "1.5px solid rgba(255,255,255,0.07)" : "1.5px solid rgba(0,0,0,0.07)",
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.025)",
        color: textSecondary, display: "flex", alignItems: "center", gap: 10,
    }}>
        {icon && <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
        <span style={{
            fontSize: 10, fontWeight: 700, color: textTertiary,
            textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0,
            padding: "2px 7px", borderRadius: 4,
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        }}>auto-filled</span>
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const CreateSparkPanel = ({ isOpen, onClose, userName, isDark, isContrast }) => {

    // Dynamic model configurations
    const [sparkModels, setSparkModels] = useState(FALLBACK_SPARK_MODELS);
    const [sparkStyles, setSparkStyles] = useState(FALLBACK_SPARK_STYLES);
    const [responseLengths, setResponseLengths] = useState(FALLBACK_RESPONSE_LENGTHS);
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
    const [configsError, setConfigsError] = useState("");

    const [form, setForm]               = useState(INITIAL_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMsg, setSuccessMsg]   = useState("");
    const [errorMsg, setErrorMsg]       = useState("");
    const [activeTab, setActiveTab]     = useState("basic");

    React.useEffect(() => {
        if (isOpen && !isLoadingConfigs) {
            loadModelConfigurations();
        }
    }, [isOpen]);

    const loadModelConfigurations = async () => {
        setIsLoadingConfigs(true);
        setConfigsError("");

        try {
            const configs = await SparkStoreModelsService.getStoreConfigs();

            // Transform and set models
            const models = SparkStoreModelsService.transformModels(configs.model_groups);
            if (models.length > 0) {
                setSparkModels(models);

                // Find default model
                const defaultModel = models.find(m => m.default);
                if (defaultModel && !form.model) {
                    setForm(f => ({ ...f, model: defaultModel.value }));
                }
            }

            // Transform and set conversation styles
            const styles = SparkStoreModelsService.transformStyles(configs.conversation_style);
            if (styles.length > 0) {
                setSparkStyles(styles);

                // Find default style
                const defaultStyle = styles.find(s => s.default);
                if (defaultStyle && !form.style) {
                    setForm(f => ({ ...f, style: defaultStyle.value }));
                }
            }

            // Transform and set response lengths
            const lengths = SparkStoreModelsService.transformResponseLengths(configs.response_length);
            if (lengths.length > 0) {
                setResponseLengths(lengths);

                // Find default length
                const defaultLength = lengths.find(l => l.default);
                if (defaultLength && !form.length) {
                    setForm(f => ({ ...f, length: defaultLength.value }));
                }
            }

            console.log("[CreateSparkPanel] Model configurations loaded", {
                models: models.length,
                styles: styles.length,
                lengths: lengths.length
            });

        } catch (error) {
            console.error("[CreateSparkPanel] Failed to load model configurations:", error);
            setConfigsError("Failed to load model configurations. Using defaults.");

            // Keep using fallback values (already set in initial state)
        } finally {
            setIsLoadingConfigs(false);
        }
    };

    // ── OneDrive file picker state ────────────────────────────────────────────
    const [isOdPickerOpen,      setIsOdPickerOpen]      = useState(false);
    const [odFolders,           setOdFolders]           = useState([]);
    const [odFiles,             setOdFiles]             = useState([]);
    const [odBreadcrumb,        setOdBreadcrumb]        = useState([{ id: "root", name: "My Files" }]);
    const [odCurrentFolderId,   setOdCurrentFolderId]   = useState("root");
    const [odIsLoadingFolders,  setOdIsLoadingFolders]  = useState(false);
    const [odIsLoadingFiles,    setOdIsLoadingFiles]    = useState(false);
    const [odError,             setOdError]             = useState("");
    const [odSelectedFiles,     setOdSelectedFiles]     = useState([]); // staging inside picker
    const [sparkFiles,          setSparkFiles]          = useState([]); // confirmed selections (max 5)

    const MAX_FILES = 5;
    const localFileInputRef = React.useRef(null);

    const handleLocalFileSelect = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length === 0) return;

        const remaining = MAX_FILES - sparkFiles.length;
        const toAdd = selected.slice(0, remaining).map(file => ({
            id: `local_${Date.now()}_${Math.random().toString(16).slice(2)}`,
            name: file.name,
            size: file.size,
            lastModified: new Date(file.lastModified).toISOString(),
            webUrl: "",
            mimeType: file.type || "application/octet-stream",
            localFile: file, // raw File object for direct upload
            isLocal: true,
        }));

        setSparkFiles(prev => [...prev, ...toAdd]);
        // Reset input so same file can be re-selected if removed
        e.target.value = "";
    };

    // ── Theme ────────────────────────────────────────────────────────────────
    const textColor     = isDark || isContrast ? "#ffffff"                : "#111827";
    const textSecondary = isDark || isContrast ? "rgba(255,255,255,0.72)" : "#374151";
    const textTertiary  = isDark || isContrast ? "rgba(255,255,255,0.38)" : "#9ca3af";
    const surfaceBg     = isDark ? "#16161f"   : isContrast ? "#000"     : "#ffffff";
    const sidebarBg     = isDark ? "#0f0f18"   : isContrast ? "#050505"  : "#f8f9fb";
    const borderColor   = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
    const inputBorder   = isDark ? "1.5px solid rgba(255,255,255,0.13)" : "1.5px solid rgba(0,0,0,0.13)";
    const inputBg       = isDark ? "rgba(255,255,255,0.045)" : "#ffffff";
    const accent        = "#0891b2";
    const accentDark    = "#0e7490";

    const ownerEmail = userName
        ? (userName.includes("@") ? userName : `${userName}@spglobal.com`)
        : "Loading...";

    const set = useCallback((key, val) => {
        setForm(f => ({ ...f, [key]: val }));
        setErrorMsg("");
    }, []);

    // Track page view for analytics when panel opens
    React.useEffect(() => {
        if (isOpen) {
            UsageLogService.trackPageView('CreateSparkPanel', {
                userRole: userName ? 'authenticated' : 'loading',
                hasSparkFiles: sparkFiles.length > 0,
                fileCount: sparkFiles.length,
                currentStep: activeTab,
                formModel: form.model,
                formStyle: form.style
            });
        }
    }, [isOpen, userName, sparkFiles.length, activeTab, form.model, form.style]);

    // ── OneDrive helpers ──────────────────────────────────────────────────────

    const odFetchFolder = async (folderId) => {
        setOdIsLoadingFolders(true);
        setOdIsLoadingFiles(true);
        setOdError("");
        setOdFolders([]);
        setOdFiles([]);

        try {
            // ── Folders ───────────────────────────────────────────────
            let fJson;
            try {
                fJson = await ApiClient.get(
                    `/api/sparkmeet/onedrive/folders?folderId=${encodeURIComponent(folderId)}`
                );
            } catch (err) {
                const clean = err.message.replace(/<[^>]+>/g, "").trim();
                throw new Error(`OneDrive folders endpoint returned ${err.status || "error"}${clean ? ": " + clean.slice(0, 120) : ""}`);
            }
            if (!fJson) throw new Error("OneDrive folders endpoint returned non-JSON. Check the server route is registered.");
            setOdFolders(fJson.folders || []);
            setOdIsLoadingFolders(false);

            // ── Files — graceful fallback if endpoint not yet wired ───
            try {
                const fiJson = await ApiClient.get(
                    `/api/sparkmeet/onedrive/files?folderId=${encodeURIComponent(folderId)}`
                );
                setOdFiles(fiJson?.files || []);
            } catch {
                setOdFiles([]); // not wired yet — silent
            }

        } catch (err) {
            // Surface a clean message — strip any HTML from the error
            const clean = err.message.replace(/<[^>]+>/g, "").trim();
            setOdError(clean);
            setOdFolders([]);
            setOdFiles([]);
        } finally {
            setOdIsLoadingFolders(false);
            setOdIsLoadingFiles(false);
        }
    };

    const odOpenPicker = async () => {
        setOdSelectedFiles([...sparkFiles]); // pre-populate with already chosen files
        setOdBreadcrumb([{ id: "root", name: "My Files" }]);
        setOdCurrentFolderId("root");
        setOdError("");
        setIsOdPickerOpen(true);
        await odFetchFolder("root");
    };

    const odNavigateFolder = async (folder) => {
        const newCrumb = { id: folder.id, name: folder.name };
        setOdBreadcrumb(prev => [...prev, newCrumb]);
        setOdCurrentFolderId(folder.id);
        await odFetchFolder(folder.id);
    };

    const odNavigateBreadcrumb = async (crumb, idx) => {
        setOdBreadcrumb(prev => prev.slice(0, idx + 1));
        setOdCurrentFolderId(crumb.id);
        await odFetchFolder(crumb.id);
    };

    const odToggleFile = (file) => {
        setOdSelectedFiles(prev => {
            const exists = prev.find(f => f.id === file.id);
            if (exists) return prev.filter(f => f.id !== file.id);
            if (prev.length >= MAX_FILES) return prev; // silently cap
            return [...prev, file];
        });
    };

    const odConfirmSelection = () => {
        setSparkFiles(odSelectedFiles);
        setIsOdPickerOpen(false);
    };

    const removeSparkFile = (id) => {
        setSparkFiles(prev => prev.filter(f => f.id !== id));
        setOdSelectedFiles(prev => prev.filter(f => f.id !== id));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const fileIcon = (name = "") => {
        const ext = name.split(".").pop().toLowerCase();
        const map = { pdf: "📄", docx: "📝", doc: "📝", xlsx: "📊", xls: "📊",
                      pptx: "📊", ppt: "📊", txt: "📃", csv: "📊", png: "🖼",
                      jpg: "🖼", jpeg: "🖼", mp4: "🎬", mp3: "🎵", zip: "🗜" };
        return map[ext] || "📁";
    };

    const resetForm = () => {
        setForm(INITIAL_FORM);
        setSuccessMsg(""); setErrorMsg(""); setActiveTab("basic");
        setSparkFiles([]); setOdSelectedFiles([]);
    };

    const handleClose = () => { resetForm(); onClose(); };

    const currentStepIdx = STEPS.findIndex(s => s.key === activeTab);
    const isLastStep     = currentStepIdx === STEPS.length - 1;
    const isFirstStep    = currentStepIdx === 0;
    const goNext = () => { if (!isLastStep)  setActiveTab(STEPS[currentStepIdx + 1].key); };
    const goPrev = () => { if (!isFirstStep) setActiveTab(STEPS[currentStepIdx - 1].key); };
    const canSubmit = !!(form.name.trim() && form.description.trim());

    const handleSubmit = async () => {
        if (!form.name.trim())             { setErrorMsg("Spark Name is required."); return; }
        if (!form.description.trim())      { setErrorMsg("Spark Description is required."); return; }
        if (form.description.length > 200) { setErrorMsg("Description must be 200 characters or less."); return; }

        setIsSubmitting(true); setErrorMsg(""); setSuccessMsg("");
        try {
            const tagsArr      = form.tags.split(",").map(t => t.trim()).filter(Boolean);
            const questionsArr = form.sampleQuestions.split("\n").map(q => q.trim()).filter(Boolean);

            const modelMap = {};
            sparkModels.forEach(model => {
                if (model.key && model.value) {
                    modelMap[model.value] = model.key;
                }
            });

            // ✅ spark_parameters: build as template literal — NOT JSON.stringify()
            // JSON.stringify() inside JSON.stringify() causes \u0022 double-encoding
            const escapedInstruction = form.customInstruction.trim().replace(/\\/g, "\\\\").replace(/"/g, '\\"');
            const modelId = modelMap[form.model] || form.model;
            const sparkParamsStr = `{ "model": "${modelId}", "length": "${form.length}", "style": "${form.style}", "top_n": 24000, "max_tokens": ${form.maxTokens}, "temperature": ${form.temperature}, "Custom instruction": "${escapedInstruction}" }`;

            // ✅ tags: Spark Catalog API expects "{tag1, tag2}" string — not JSON array
            const tagsStr = tagsArr.length > 0
                ? `{${tagsArr.join(", ")}}`
                : "{}";

            // ✅ sample_questions: Spark Catalog API expects {"q1", "q2"} string — not JSON array
            const questionsStr = questionsArr.length > 0
                ? `{${questionsArr.map(q => `"${q.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`).join(", ")}}`
                : "{}";

            const payload = {
                params: {
                    spark_name: form.name.trim(),
                    spark_owner: ownerEmail,
                    spark_description: form.description.trim(),
                    spark_parameters: sparkParamsStr,
                    collection_name: form.name.trim(),
                    collection_owner: ownerEmail,
                    shared_with: "",
                    shared_with_type: "user",
                    sharing_type: "write",
                    shared_by: ownerEmail,
                    tags: tagsStr,
                    sample_questions: questionsStr,
                    Operation: "Insert",
                }
            };

            // ── Create Spark ─────────────────────────────────────────
            const raw = await ApiClient.post("/api/sparkstore/sparks/create", payload);
            // Spark Catalog API may return a double-encoded string
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            const sparkId = parsed?.spark_id ?? parsed?.sparkId ?? null;
            const collectionId = parsed?.collection_id ?? parsed?.collectionId ?? null;
            console.log(`[CreateSpark] spark_id=${sparkId} collection_id=${collectionId}`);

            // ── Upload OneDrive files to Spark document collection ──
            if (collectionId && sparkFiles.length > 0) {
                setSuccessMsg(`✅ Spark "${form.name}" created! Uploading ${sparkFiles.length} document(s)…`);

                const uploadResults = await Promise.allSettled(
                    sparkFiles.map(async (file) => {
                        if (file.isLocal && file.localFile) {
                            // ── Local file: send as multipart directly ──
                            const formData = new FormData();
                            formData.append("file", file.localFile, file.name);
                            formData.append("collectionId", String(collectionId));
                            formData.append("fileName", file.name);
                            formData.append("owner", ownerEmail);
                            // No webUrl for local files — tagging will be empty

                            const uploadResp = await fetch("/api/sparkstore/documents/upload-local", {
                                method: "POST",
                                headers: { Authorization: `Bearer ${await AuthTokenProvider.getAccessToken() }` },
                                // ⚠️ Do NOT set Content-Type — browser sets multipart boundary automatically
                                body: formData,
                            });
                            if (!uploadResp.ok) {
                                const errText = await uploadResp.text();
                                throw new Error(`${file.name}: ${errText}`);
                            }
                        } else {
                            // ── OneDrive file: JSON — use ApiClient ───────────────────────────────
                            await ApiClient.post("/api/sparkstore/documents/upload", {
                                collectionId,
                                fileId: file.id,
                                fileName: file.name,
                                webUrl: file.webUrl ?? "",
                                owner: ownerEmail,
                            });
                        }
                        return file.name;
                    })
                );

                const failed = uploadResults.filter(r => r.status === "rejected");
                const succeeded = uploadResults.filter(r => r.status === "fulfilled");

                if (failed.length === 0) {
                    setSuccessMsg(`✅ Spark "${form.name}" created with ${succeeded.length} document(s) uploaded!`);
                } else {
                    setSuccessMsg(`✅ Spark created. ${succeeded.length} document(s) uploaded. ⚠ ${failed.length} failed: ${failed.map(f => f.reason?.message).join(", ")}`);
                }
            } else {
                if (sparkFiles.length > 0 && !collectionId) {
                    // collectionId missing — Spark was created but collection not returned
                    // (e.g. duplicate name, collection provisioning failed, or unexpected API response)
                    setSuccessMsg(`✅ Spark "${form.name}" created! ⚠ Documents were not uploaded — collection ID was not returned by the API. You can add documents manually from the Spark Store.`);
                    console.warn("[CreateSpark] collectionId missing from Create response — files not uploaded.", { sparkId, collectionId });
                } else {
                    setSuccessMsg(`✅ Spark "${form.name}" created! It will appear in the Spark Store shortly.`);
                }
            }

            setForm(INITIAL_FORM); setActiveTab("basic"); setSparkFiles([]);
            // Close the panel after a short delay so the user sees the success message
            setTimeout(() => { onClose(); }, 2200);
        } catch (err) {
            setErrorMsg(`Failed: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const inputStyle = {
        width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
        border: inputBorder, background: inputBg, color: textColor,
        outline: "none", boxSizing: "border-box", fontFamily: "inherit",
        transition: "border-color 0.15s, box-shadow 0.15s",
    };
    const textareaStyle = {
        ...inputStyle, resize: "vertical", lineHeight: 1.65,
        background: isDark ? "rgba(0,0,0,0.18)" : "#fafafa",
    };

    const hintBox = (text) => (
        <div style={{
            marginTop: 8, fontSize: 12, color: textTertiary, fontStyle: "italic",
            padding: "6px 10px", borderRadius: 7,
            background: isDark ? `${accent}0c` : `${accent}08`,
            border: `1px solid ${accent}18`,
        }}>{text}</div>
    );

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 500,
            display: "flex", flexDirection: "column",
            backgroundColor: surfaceBg,
            animation: "cspIn 0.22s cubic-bezier(0.16,1,0.3,1)",
        }}>
            <style>{`
                @keyframes cspIn   { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
                @keyframes cspSpin { to   { transform: rotate(360deg); } }
                .csp-input:focus   { border-color: ${accent} !important; box-shadow: 0 0 0 3px ${accent}22 !important; }
            `}</style>

            {/* ── HEADER ── */}
            <div style={{
                display: "flex", alignItems: "center", gap: 16, padding: "14px 28px",
                background: isDark
                    ? "linear-gradient(135deg,rgba(8,145,178,0.18),rgba(14,116,144,0.09))"
                    : "linear-gradient(135deg,rgba(8,145,178,0.09),rgba(14,116,144,0.04))",
                borderBottom: `1px solid ${borderColor}`, flexShrink: 0,
            }}>
                <div style={{
                    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                    background: `linear-gradient(135deg,${accent},${accentDark})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: `0 4px 16px ${accent}44`,
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.6" strokeLinecap="round"/>
                    </svg>
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: textColor, letterSpacing: "-0.3px" }}>Create Spark</div>
                    <div style={{ fontSize: 12, color: textSecondary, marginTop: 1 }}>
                        Build and publish a new AI assistant · Step {currentStepIdx + 1} of {STEPS.length}
                    </div>
                </div>
                <button type="button" onClick={handleClose} style={{
                    border: "none", background: "transparent", cursor: "pointer",
                    color: textSecondary, fontSize: 22, padding: "4px 10px",
                    borderRadius: 8, lineHeight: 1, flexShrink: 0, transition: "background 0.13s",
                }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >✕</button>
            </div>

            {/* ── BODY ── */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

                {/* LEFT WIZARD SIDEBAR */}
                <div style={{
                    width: 240, flexShrink: 0, background: sidebarBg,
                    borderRight: `1px solid ${borderColor}`,
                    display: "flex", flexDirection: "column",
                    padding: "28px 16px", gap: 4,
                }}>
                    {STEPS.map((step, i) => {
                        const done    = i < currentStepIdx;
                        const current = step.key === activeTab;
                        return (
                            <div key={step.key} onClick={() => setActiveTab(step.key)} style={{
                                display: "flex", alignItems: "flex-start", gap: 14,
                                padding: "12px 14px", borderRadius: 12, cursor: "pointer",
                                background: current ? (isDark ? `${accent}1a` : `${accent}0e`) : "transparent",
                                border: current ? `1.5px solid ${accent}30` : "1.5px solid transparent",
                                transition: "all 0.15s ease",
                            }}
                                onMouseEnter={e => { if (!current) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                onMouseLeave={e => { if (!current) e.currentTarget.style.background = "transparent"; }}
                            >
                                <div style={{
                                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: done ? 14 : 13, fontWeight: 800,
                                    background: current ? accent : done ? `${accent}22` : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"),
                                    color: current ? "#fff" : done ? accent : textTertiary,
                                    border: done ? `1.5px solid ${accent}44` : "1.5px solid transparent",
                                    boxShadow: current ? `0 3px 10px ${accent}44` : "none",
                                    transition: "all 0.2s ease",
                                }}>
                                    {done ? "✓" : step.num}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontSize: 13, fontWeight: current ? 700 : 500,
                                        color: current ? accent : done ? textSecondary : textTertiary,
                                    }}>{step.label}</div>
                                    <div style={{ fontSize: 11, color: textTertiary, marginTop: 2, lineHeight: 1.4 }}>{step.desc}</div>
                                </div>
                            </div>
                        );
                    })}

                    <div style={{ flex: 1 }} />

                    {/* Live preview card */}
                    {form.name && (
                        <div style={{
                            padding: "14px", borderRadius: 12, marginTop: 8,
                            background: isDark ? `${accent}12` : `${accent}07`,
                            border: `1.5px solid ${accent}25`,
                        }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>
                                Spark Preview
                            </div>
                            {[["Name", form.name], ["Model", form.model], ["Style", form.style]].map(([k, v]) => (
                                <div key={k} style={{ display: "flex", gap: 6, fontSize: 11, marginBottom: 5 }}>
                                    <span style={{ color: textTertiary, minWidth: 36 }}>{k}:</span>
                                    <span style={{ color: textSecondary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT FORM AREA */}
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>

                    {/* Banners */}
                    {(successMsg || errorMsg) && (
                        <div style={{ padding: "16px 40px 0", flexShrink: 0 }}>
                            {successMsg && (
                                <div style={{ padding: "13px 18px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                                    background: "rgba(22,163,74,0.10)", border: "1.5px solid rgba(22,163,74,0.28)",
                                    color: isDark ? "#86efac" : "#15803d" }}>{successMsg}</div>
                            )}
                            {errorMsg && (
                                <div style={{ padding: "12px 18px", borderRadius: 10, fontSize: 14,
                                    background: "rgba(220,38,38,0.09)", border: "1.5px solid rgba(220,38,38,0.24)",
                                    color: isDark ? "#fca5a5" : "#b91c1c" }}>{errorMsg}</div>
                            )}
                        </div>
                    )}

                    {/* Form content */}
                    <div style={{ flex: 1, padding: "32px 40px 24px", maxWidth: 860, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>

                        {/* Step heading */}
                        <div style={{ marginBottom: 28 }}>
                            <div style={{ fontSize: 22, fontWeight: 800, color: textColor, letterSpacing: "-0.3px" }}>
                                {STEPS[currentStepIdx].label}
                            </div>
                            <div style={{ fontSize: 14, color: textSecondary, marginTop: 4 }}>
                                {STEPS[currentStepIdx].desc}
                            </div>
                        </div>

                        {/* ─── STEP 1: BASIC INFO ─── */}
                        {activeTab === "basic" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                    <div>
                                        <FieldLabel required textSecondary={textSecondary} textTertiary={textTertiary}>
                                            Spark Name
                                        </FieldLabel>
                                        <input className="csp-input" value={form.name}
                                            onChange={e => set("name", e.target.value)}
                                            placeholder="e.g. MI-SAM Assist, HR Policy Bot"
                                            style={inputStyle} />
                                    </div>
                                    <div>
                                        <FieldLabel textSecondary={textSecondary} textTertiary={textTertiary}>
                                            Spark Owner
                                        </FieldLabel>
                                        <ReadOnlyField value={ownerEmail} icon="👤"
                                            isDark={isDark} textSecondary={textSecondary} textTertiary={textTertiary} />
                                    </div>
                                </div>
                                <div>
                                    <FieldLabel required
                                        hint="— shown to users in the Spark Store"
                                        charCount={form.description.length} maxChars={200}
                                        textSecondary={textSecondary} textTertiary={textTertiary}>
                                        Spark Description
                                    </FieldLabel>
                                    <textarea className="csp-input" value={form.description}
                                        onChange={e => { if (e.target.value.length <= 200) set("description", e.target.value); }}
                                        placeholder="Hi, I am [Spark Name]. I help S&P Global teams by..."
                                        rows={5} style={textareaStyle} />
                                    {form.description.length > 170 && (
                                        <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 4, fontWeight: 600 }}>
                                            ⚠ {200 - form.description.length} characters remaining
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── STEP 2: PARAMETERS ─── */}
                        {activeTab === "params" && (                          
                            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                                {isLoadingConfigs && (
                                    <div style={{
                                        padding: "10px 14px", borderRadius: 8, fontSize: 12,
                                        background: isDark ? `${accent}12` : `${accent}08`,
                                        border: `1px solid ${accent}25`,
                                        color: textSecondary,
                                        display: "flex", alignItems: "center", gap: 8
                                    }}>
                                        <div style={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${accent}33`, borderTopColor: accent, animation: "cspSpin 0.7s linear infinite" }} />
                                        Loading model configurations...
                                    </div>
                                )}
                                {configsError && (
                                    <div style={{
                                        padding: "10px 14px", borderRadius: 8, fontSize: 12,
                                        background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.24)",
                                        color: isDark ? "#fbbf24" : "#d97706"
                                    }}>
                                        ⚠ {configsError}
                                    </div>
                                )}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
                                    <div>
                                        <FieldLabel textSecondary={textSecondary} textTertiary={textTertiary}>Model Selection</FieldLabel>
                                        <StyledSelect value={form.model} onChange={v => set("model", v)}
                                            options={sparkModels} isDark={isDark} textColor={textColor} />
                                        {hintBox(sparkModels.find(m => m.value === form.model)?.desc)}
                                    </div>
                                    <div>
                                        <FieldLabel textSecondary={textSecondary} textTertiary={textTertiary}>Response Style</FieldLabel>
                                        <StyledSelect value={form.style} onChange={v => set("style", v)}
                                            options={sparkStyles} isDark={isDark} textColor={textColor} />
                                        {hintBox(sparkStyles.find(s => s.value === form.style)?.desc)}
                                    </div>
                                    <div>
                                        <FieldLabel textSecondary={textSecondary} textTertiary={textTertiary}>Response Length</FieldLabel>
                                        <StyledSelect value={form.length} onChange={v => set("length", v)}
                                            options={responseLengths} isDark={isDark} textColor={textColor} />
                                        {hintBox(responseLengths.find(l => l.value === form.length)?.desc)}
                                    </div>
                                </div>
                                {/* Max Tokens / Temperature / Top N are intentionally hidden — fixed system defaults */}
                                <div>
                                    <FieldLabel hint="— the hidden system prompt for every conversation"
                                        textSecondary={textSecondary} textTertiary={textTertiary}>
                                        Instructions (Behavior)
                                    </FieldLabel>
                                    <textarea className="csp-input" value={form.customInstruction}
                                        onChange={e => set("customInstruction", e.target.value)}
                                        placeholder={`You are [Spark Name], a tool designed to help S&P Global teams with...\n\nInstructions:\n- Base all answers strictly on provided documentation\n- Provide concise, relevant responses\n- If information is unavailable, clearly state it`}
                                        rows={9}
                                        style={{ ...textareaStyle, fontFamily: "ui-monospace,'Cascadia Code',Consolas,monospace", fontSize: 13 }} />
                                    <div style={{ fontSize: 11, color: textTertiary, marginTop: 5 }}>
                                        {form.customInstruction.length.toLocaleString()} characters
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ─── STEP 3: DISCOVERY ─── */}
                        {activeTab === "discovery" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                                <div>
                                    <FieldLabel hint="comma-separated · helps users find this Spark"
                                        textSecondary={textSecondary} textTertiary={textTertiary}>
                                        Tags
                                    </FieldLabel>
                                    <input className="csp-input" value={form.tags}
                                        onChange={e => set("tags", e.target.value)}
                                        placeholder="e.g. HR, Policy, SAM, Integrations, Compliance"
                                        style={inputStyle} />
                                    {form.tags.trim() && (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                                            {form.tags.split(",").map(t => t.trim()).filter(Boolean).map((tag, i) => (
                                                <span key={i} style={{
                                                    padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                                                    background: isDark ? `${accent}18` : `${accent}0e`,
                                                    border: `1px solid ${accent}30`,
                                                    color: isDark ? "#67e8f9" : "#0e7490",
                                                }}>{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <FieldLabel hint="one per line · shown as conversation starters"
                                        textSecondary={textSecondary} textTertiary={textTertiary}>
                                        Sample Questions
                                    </FieldLabel>
                                    <textarea className="csp-input" value={form.sampleQuestions}
                                        onChange={e => set("sampleQuestions", e.target.value)}
                                        placeholder={"How do I integrate with the SAM system?\nWhat authentication methods are supported?\nWhat are the key differences between SAML 2.0 and OIDC?\nHow do I refresh an access token?"}
                                        rows={6} style={textareaStyle} />
                                    {form.sampleQuestions.trim() && (
                                        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: textTertiary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Preview</div>
                                            {form.sampleQuestions.split("\n").map(q => q.trim()).filter(Boolean).map((q, i) => (
                                                <div key={i} style={{
                                                    padding: "10px 14px", borderRadius: 9, fontSize: 13,
                                                    background: isDark ? `${accent}0c` : `${accent}08`,
                                                    border: `1px solid ${accent}20`,
                                                    color: textSecondary,
                                                    display: "flex", alignItems: "flex-start", gap: 10,
                                                }}>
                                                    <span style={{ color: accent, fontWeight: 800, flexShrink: 0 }}>?</span>
                                                    {q}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Summary card */}
                                {form.name && (
                                    <div style={{
                                        padding: "18px 20px", borderRadius: 12,
                                        background: isDark ? `${accent}12` : `${accent}07`,
                                        border: `1.5px solid ${accent}25`,
                                    }}>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 14 }}>
                                            ✦ Spark Summary — Ready to create
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                                            {[
                                                ["Name",  form.name],  ["Owner", ownerEmail],
                                                ["Model", form.model], ["Style", form.style],
                                                ["Length", form.length],
                                            ].map(([k, v]) => (
                                                <div key={k} style={{ display: "flex", gap: 8, fontSize: 13 }}>
                                                    <span style={{ color: textTertiary, flexShrink: 0, minWidth: 48 }}>{k}:</span>
                                                    <span style={{ color: textColor, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── OneDrive Document Attachments ── */}
                                <div>
                                    <FieldLabel
                                        hint={`— knowledge files for this Spark (max ${MAX_FILES})`}
                                        textSecondary={textSecondary} textTertiary={textTertiary}>
                                        Source Documents
                                    </FieldLabel>

                                    {/* Selected files list */}
                                    {sparkFiles.length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                                            {sparkFiles.map(file => (
                                                <div key={file.id} style={{
                                                    display: "flex", alignItems: "center", gap: 10,
                                                    padding: "9px 12px", borderRadius: 9,
                                                    background: isDark ? "rgba(8,145,178,0.10)" : "rgba(8,145,178,0.06)",
                                                    border: `1px solid ${accent}25`,
                                                }}>
                                                    <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(file.name)}</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {file.name}
                                                        </div>
                                                        {file.size && (
                                                            <div style={{ fontSize: 11, color: textTertiary, marginTop: 1 }}>
                                                                {formatFileSize(file.size)}
                                                                {file.lastModified && ` · Modified ${new Date(file.lastModified).toLocaleDateString()}`}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button type="button" onClick={() => removeSparkFile(file.id)}
                                                        title="Remove file"
                                                        style={{
                                                            border: "none", background: "transparent", cursor: "pointer",
                                                            color: textTertiary, fontSize: 18, lineHeight: 1,
                                                            padding: "2px 4px", borderRadius: 4, flexShrink: 0,
                                                            transition: "color 0.13s",
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                                                        onMouseLeave={e => e.currentTarget.style.color = textTertiary}
                                                    >×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* ── Two upload buttons side by side ── */}
                                    <div style={{ display: "flex", gap: 10 }}>

                                        {/* Add from OneDrive */}
                                        <button type="button" onClick={odOpenPicker}
                                            disabled={sparkFiles.length >= MAX_FILES}
                                            style={{
                                                flex: 1, display: "flex", alignItems: "center", gap: 10,
                                                padding: "11px 16px", borderRadius: 10,
                                                cursor: sparkFiles.length >= MAX_FILES ? "not-allowed" : "pointer",
                                                border: isDark ? "1.5px dashed rgba(255,255,255,0.18)" : "1.5px dashed rgba(0,0,0,0.18)",
                                                background: "transparent",
                                                opacity: sparkFiles.length >= MAX_FILES ? 0.45 : 1,
                                                transition: "all 0.15s",
                                            }}
                                            onMouseEnter={e => { if (sparkFiles.length < MAX_FILES) { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = isDark ? `${accent}0d` : `${accent}08`; } }}
                                            onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)"; e.currentTarget.style.background = "transparent"; }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M9.5 13.5 5 11.2A5.5 5.5 0 0 1 14.8 7a4 4 0 0 1 5.2 3.8v.2l-10.5 2.5Z" fill="#0364B8" />
                                                <path d="M9.5 13.5 20 11l.5 1A4 4 0 0 1 16.5 18H7.5a3.5 3.5 0 0 1-2.5-6l4.5 1.5Z" fill="#0078D4" />
                                                <path d="M14.8 7a4 4 0 0 1 5.2 3.8L20 11l-5-2-.2-2Z" fill="#1490DF" />
                                                <path d="M5 11.2A5.5 5.5 0 0 1 14.8 7L14.6 9A3.5 3.5 0 0 0 9 11.5l-4-.3Z" fill="#28A8E8" />
                                            </svg>
                                            <div style={{ flex: 1, textAlign: "left" }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: textColor }}>+ OneDrive</div>
                                                <div style={{ fontSize: 11, color: textTertiary, marginTop: 1 }}>Browse cloud files</div>
                                            </div>
                                        </button>

                                        {/* Upload from Computer */}
                                        <button type="button"
                                            onClick={() => localFileInputRef.current?.click()}
                                            disabled={sparkFiles.length >= MAX_FILES}
                                            style={{
                                                flex: 1, display: "flex", alignItems: "center", gap: 10,
                                                padding: "11px 16px", borderRadius: 10,
                                                cursor: sparkFiles.length >= MAX_FILES ? "not-allowed" : "pointer",
                                                border: isDark ? "1.5px dashed rgba(255,255,255,0.18)" : "1.5px dashed rgba(0,0,0,0.18)",
                                                background: "transparent",
                                                opacity: sparkFiles.length >= MAX_FILES ? 0.45 : 1,
                                                transition: "all 0.15s",
                                            }}
                                            onMouseEnter={e => { if (sparkFiles.length < MAX_FILES) { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = isDark ? "rgba(124,58,237,0.08)" : "rgba(124,58,237,0.05)"; } }}
                                            onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)"; e.currentTarget.style.background = "transparent"; }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: "#7c3aed" }}
                                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <div style={{ flex: 1, textAlign: "left" }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: textColor }}>+ Upload File</div>
                                                <div style={{ fontSize: 11, color: textTertiary, marginTop: 1 }}>From your computer</div>
                                            </div>
                                        </button>

                                        {/* Hidden file input */}
                                        <input
                                            ref={localFileInputRef}
                                            type="file"
                                            multiple
                                            accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.pptx,.ppt,.md,.json,.xml,.html"
                                            onChange={handleLocalFileSelect}
                                            style={{ display: "none" }}
                                        />
                                    </div>

                                    {/* File count hint */}
                                    <div style={{ fontSize: 11, color: textTertiary, marginTop: 6, textAlign: "right" }}>
                                        {sparkFiles.length}/{MAX_FILES} files selected
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── ONEDRIVE FILE PICKER MODAL ──────────────────────────────── */}
            {isOdPickerOpen && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 600,
                    background: "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 20,
                }}
                    onClick={e => { if (e.target === e.currentTarget) setIsOdPickerOpen(false); }}
                >
                    <div style={{
                        width: "min(720px, 96vw)", height: "min(580px, 90vh)",
                        borderRadius: 14, display: "flex", flexDirection: "column",
                        background: surfaceBg,
                        boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.7)" : "0 20px 60px rgba(0,0,0,0.22)",
                        border: `1px solid ${borderColor}`,
                        overflow: "hidden",
                        animation: "cspIn 0.18s cubic-bezier(0.16,1,0.3,1)",
                    }}>

                        {/* Modal header */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                            borderBottom: `1px solid ${borderColor}`,
                            background: isDark ? "rgba(0,120,212,0.12)" : "rgba(0,120,212,0.06)",
                            flexShrink: 0,
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M9.5 13.5 5 11.2A5.5 5.5 0 0 1 14.8 7a4 4 0 0 1 5.2 3.8v.2l-10.5 2.5Z" fill="#0364B8"/>
                                <path d="M9.5 13.5 20 11l.5 1A4 4 0 0 1 16.5 18H7.5a3.5 3.5 0 0 1-2.5-6l4.5 1.5Z" fill="#0078D4"/>
                                <path d="M14.8 7a4 4 0 0 1 5.2 3.8L20 11l-5-2-.2-2Z" fill="#1490DF"/>
                                <path d="M5 11.2A5.5 5.5 0 0 1 14.8 7L14.6 9A3.5 3.5 0 0 0 9 11.5l-4-.3Z" fill="#28A8E8"/>
                            </svg>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>Select from OneDrive</div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 1 }}>
                                    Choose up to {MAX_FILES} knowledge documents · {odSelectedFiles.length}/{MAX_FILES} selected
                                </div>
                            </div>
                            <button type="button" onClick={() => setIsOdPickerOpen(false)} style={{
                                border: "none", background: "transparent", cursor: "pointer",
                                color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 6,
                                lineHeight: 1,
                            }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            >✕</button>
                        </div>

                        {/* Breadcrumb navigation */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap",
                            padding: "8px 18px",
                            borderBottom: `1px solid ${borderColor}`,
                            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                            flexShrink: 0,
                        }}>
                            {odBreadcrumb.map((crumb, idx) => (
                                <React.Fragment key={crumb.id}>
                                    <button type="button"
                                        onClick={() => odNavigateBreadcrumb(crumb, idx)}
                                        style={{
                                            border: "none", background: "transparent", cursor: "pointer",
                                            fontSize: 12, fontWeight: idx === odBreadcrumb.length - 1 ? 700 : 500,
                                            color: idx === odBreadcrumb.length - 1 ? accent : textSecondary,
                                            padding: "3px 6px", borderRadius: 5,
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    >
                                        {idx === 0 ? "🏠 " : ""}{crumb.name}
                                    </button>
                                    {idx < odBreadcrumb.length - 1 && (
                                        <span style={{ color: textTertiary, fontSize: 11 }}>›</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Error */}
                        {odError && (
                            <div style={{
                                margin: "8px 18px 0", padding: "8px 12px", borderRadius: 8, fontSize: 12,
                                background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.24)",
                                color: isDark ? "#fca5a5" : "#b91c1c", flexShrink: 0,
                            }}>{odError}</div>
                        )}

                        {/* File / folder list */}
                        <div style={{ flex: 1, overflowY: "auto", padding: "6px 10px" }}>
                            {(odIsLoadingFolders || odIsLoadingFiles) ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", gap: 10, color: textTertiary }}>
                                    <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2.5px solid ${accent}33`, borderTopColor: accent, animation: "cspSpin 0.7s linear infinite" }} />
                                    <span style={{ fontSize: 13 }}>Loading…</span>
                                </div>
                            ) : (odFolders.length === 0 && odFiles.length === 0) ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 8, color: textTertiary }}>
                                    <span style={{ fontSize: 32 }}>📂</span>
                                    <span style={{ fontSize: 13 }}>This folder is empty</span>
                                </div>
                            ) : (
                                <>
                                    {/* Folders */}
                                    {odFolders.map(folder => (
                                        <div key={folder.id}
                                            onClick={() => odNavigateFolder(folder)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 12,
                                                padding: "9px 10px", borderRadius: 8, cursor: "pointer",
                                                transition: "background 0.12s",
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"}
                                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                        >
                                            <span style={{ fontSize: 20, flexShrink: 0 }}>📁</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 500, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {folder.name}
                                                </div>
                                                {folder.itemCount != null && (
                                                    <div style={{ fontSize: 10, color: textTertiary, marginTop: 1 }}>
                                                        {folder.itemCount} item{folder.itemCount !== 1 ? "s" : ""}
                                                    </div>
                                                )}
                                            </div>
                                            <span style={{ color: textTertiary, fontSize: 14 }}>›</span>
                                        </div>
                                    ))}

                                    {/* Divider between folders and files */}
                                    {odFolders.length > 0 && odFiles.length > 0 && (
                                        <div style={{ height: 1, background: borderColor, margin: "4px 10px 4px" }} />
                                    )}

                                    {/* Files */}
                                    {odFiles.map(file => {
                                        const isSelected   = !!odSelectedFiles.find(f => f.id === file.id);
                                        const isAtCap      = !isSelected && odSelectedFiles.length >= MAX_FILES;
                                        return (
                                            <div key={file.id}
                                                onClick={() => !isAtCap && odToggleFile(file)}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 12,
                                                    padding: "9px 10px", borderRadius: 8,
                                                    cursor: isAtCap ? "not-allowed" : "pointer",
                                                    opacity: isAtCap ? 0.4 : 1,
                                                    background: isSelected ? (isDark ? `${accent}18` : `${accent}0d`) : "transparent",
                                                    border: isSelected ? `1px solid ${accent}30` : "1px solid transparent",
                                                    transition: "all 0.12s",
                                                }}
                                                onMouseEnter={e => { if (!isAtCap && !isSelected) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"; }}
                                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                                            >
                                                {/* Checkbox */}
                                                <div style={{
                                                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                                    border: isSelected ? `2px solid ${accent}` : isDark ? "1.5px solid rgba(255,255,255,0.28)" : "1.5px solid rgba(0,0,0,0.22)",
                                                    background: isSelected ? accent : "transparent",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    transition: "all 0.12s",
                                                }}>
                                                    {isSelected && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>✓</span>}
                                                </div>
                                                <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(file.name)}</span>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: isSelected ? accent : textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {file.name}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: textTertiary, marginTop: 1 }}>
                                                        {formatFileSize(file.size)}
                                                        {file.lastModified ? ` · ${new Date(file.lastModified).toLocaleDateString()}` : ""}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Placeholder when files endpoint not yet wired */}
                                    {odFiles.length === 0 && odFolders.length > 0 && !odIsLoadingFiles && (
                                        <div style={{ padding: "12px 10px", fontSize: 12, color: textTertiary, fontStyle: "italic", textAlign: "center" }}>
                                            Navigate into a folder to see files
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "12px 18px", borderTop: `1px solid ${borderColor}`,
                            background: isDark ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.02)",
                            flexShrink: 0, gap: 10,
                        }}>
                            {/* Selection summary */}
                            <div style={{ fontSize: 12, color: textSecondary }}>
                                {odSelectedFiles.length === 0 ? (
                                    <span style={{ color: textTertiary }}>No files selected</span>
                                ) : (
                                    <span>
                                        <span style={{ fontWeight: 700, color: accent }}>{odSelectedFiles.length}</span>
                                        {` / ${MAX_FILES} file${odSelectedFiles.length !== 1 ? "s" : ""} selected`}
                                    </span>
                                )}
                                {odSelectedFiles.length >= MAX_FILES && (
                                    <span style={{ color: "#f59e0b", marginLeft: 8, fontWeight: 600 }}>· Maximum reached</span>
                                )}
                            </div>

                            <div style={{ display: "flex", gap: 8 }}>
                                <button type="button" onClick={() => setIsOdPickerOpen(false)} style={{
                                    padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                    background: "transparent", color: textSecondary, cursor: "pointer",
                                }}
                                    onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                >Cancel</button>

                                <button type="button" onClick={odConfirmSelection}
                                    disabled={odSelectedFiles.length === 0}
                                    style={{
                                        padding: "9px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                                        border: "none",
                                        background: odSelectedFiles.length === 0
                                            ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
                                            : `linear-gradient(135deg,${accent},${accentDark})`,
                                        color: odSelectedFiles.length === 0 ? textTertiary : "#fff",
                                        cursor: odSelectedFiles.length === 0 ? "not-allowed" : "pointer",
                                        boxShadow: odSelectedFiles.length > 0 ? `0 3px 12px ${accent}44` : "none",
                                        transition: "all 0.13s",
                                    }}
                                    onMouseEnter={e => { if (odSelectedFiles.length > 0) e.currentTarget.style.transform = "scale(1.02)"; }}
                                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                >
                                    Attach {odSelectedFiles.length > 0 ? `${odSelectedFiles.length} File${odSelectedFiles.length !== 1 ? "s" : ""}` : "Files"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── FOOTER ── */}
            <div style={{ flexShrink: 0, borderTop: `1px solid ${borderColor}`, background: isDark ? "rgba(0,0,0,0.22)" : "rgba(0,0,0,0.015)" }}>
                {/* Progress bar */}
                <div style={{ height: 3, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
                    <div style={{
                        height: "100%",
                        width: `${((currentStepIdx + 1) / STEPS.length) * 100}%`,
                        background: `linear-gradient(90deg,${accent},${accentDark})`,
                        transition: "width 0.35s cubic-bezier(0.4,0,0.2,1)",
                    }} />
                </div>

                <div style={{ display: "flex", alignItems: "center", padding: "16px 28px", gap: 16 }}>

                    {/* Clickable step breadcrumb */}
                    <div style={{ flex: 1, display: "flex", gap: 6, alignItems: "center" }}>
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s.key}>
                                <div onClick={() => setActiveTab(s.key)} style={{
                                    display: "flex", alignItems: "center", gap: 7, cursor: "pointer",
                                    padding: "5px 10px", borderRadius: 7,
                                    background: s.key === activeTab
                                        ? (isDark ? `${accent}18` : `${accent}0d`)
                                        : "transparent",
                                    border: s.key === activeTab ? `1px solid ${accent}28` : "1px solid transparent",
                                    transition: "all 0.15s",
                                }}
                                    onMouseEnter={e => { if (s.key !== activeTab) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                    onMouseLeave={e => { if (s.key !== activeTab) e.currentTarget.style.background = "transparent"; }}
                                >
                                    <div style={{
                                        width: 20, height: 20, borderRadius: "50%",
                                        background: i < currentStepIdx ? accent : s.key === activeTab ? accent : (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"),
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 10, fontWeight: 800,
                                        color: i <= currentStepIdx ? "#fff" : textTertiary,
                                        transition: "background 0.2s", flexShrink: 0,
                                    }}>
                                        {i < currentStepIdx ? "✓" : s.num}
                                    </div>
                                    <span style={{
                                        fontSize: 12, fontWeight: s.key === activeTab ? 700 : 400,
                                        color: s.key === activeTab ? textColor : textSecondary,
                                        whiteSpace: "nowrap",
                                    }}>{s.label}</span>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div style={{ width: 16, height: 1.5, background: i < currentStepIdx ? accent : borderColor, borderRadius: 1, flexShrink: 0 }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Action buttons — large and prominent */}
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
                        <button type="button" onClick={handleClose} style={{
                            padding: "11px 22px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                            background: "transparent", color: textSecondary, cursor: "pointer", transition: "background 0.13s",
                        }}
                            onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >Cancel</button>

                        {!isFirstStep && (
                            <button type="button" onClick={goPrev} style={{
                                padding: "11px 24px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
                                color: textColor, cursor: "pointer", transition: "background 0.13s",
                                display: "flex", alignItems: "center", gap: 7,
                            }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.08)"}
                                onMouseLeave={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)"}
                            >
                                <span style={{ fontSize: 17 }}>‹</span> Back
                            </button>
                        )}

                        {!isLastStep ? (
                            <button type="button" onClick={goNext} style={{
                                padding: "11px 32px", borderRadius: 9, fontSize: 14, fontWeight: 700,
                                border: "none", cursor: "pointer",
                                background: `linear-gradient(135deg,${accent},${accentDark})`,
                                color: "#fff", boxShadow: `0 3px 14px ${accent}44`,
                                display: "flex", alignItems: "center", gap: 7,
                                transition: "transform 0.13s, box-shadow 0.13s",
                            }}
                                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = `0 5px 18px ${accent}55`; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = `0 3px 14px ${accent}44`; }}
                            >
                                Next <span style={{ fontSize: 17 }}>›</span>
                            </button>
                        ) : (
                            <button type="button" onClick={handleSubmit}
                                disabled={isSubmitting || !canSubmit}
                                style={{
                                    padding: "11px 34px", borderRadius: 9, fontSize: 14, fontWeight: 800,
                                    border: "none",
                                    cursor: isSubmitting || !canSubmit ? "not-allowed" : "pointer",
                                    background: isSubmitting || !canSubmit
                                        ? (isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)")
                                        : `linear-gradient(135deg,${accent},${accentDark})`,
                                    color: isSubmitting || !canSubmit ? textTertiary : "#fff",
                                    boxShadow: canSubmit && !isSubmitting ? `0 4px 18px ${accent}50` : "none",
                                    display: "flex", alignItems: "center", gap: 8,
                                    transition: "transform 0.13s, box-shadow 0.13s",
                                }}
                                onMouseEnter={e => { if (canSubmit && !isSubmitting) { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = `0 6px 22px ${accent}60`; } }}
                                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = canSubmit && !isSubmitting ? `0 4px 18px ${accent}50` : "none"; }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div style={{ width: 15, height: 15, borderRadius: "50%", border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "cspSpin 0.7s linear infinite" }} />
                                        Creating…
                                    </>
                                ) : <>✦ Create Spark</>}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateSparkPanel;

/*
──────────────────────────────────────────────────────────────
CHATPANEL.JSX WIRING — unchanged from v1, same 4 changes:

1. import CreateSparkPanel from "./CreateSparkPanel";
2. const [isSparkCreateOpen, setIsSparkCreateOpen] = useState(false);
3. Sidebar button onClick={() => setIsSparkCreateOpen(true)}
4. <CreateSparkPanel isOpen={isSparkCreateOpen} onClose={() => setIsSparkCreateOpen(false)}
       userName={userName} getTeamsSsoToken={getTeamsSsoToken} isDark={isDark} isContrast={isContrast} />
──────────────────────────────────────────────────────────────

SERVER-SIDE — ONE NEW ENDPOINT NEEDED for file listing:
The picker already uses:
  GET /api/sparkmeet/onedrive/folders?folderId=   ← EXISTING (already in SparkMeetHandlers)

New endpoint needed (add to SparkMeetHandlers.cs):
  GET /api/sparkmeet/onedrive/files?folderId=

It should return:
{
  "files": [
    {
      "id": "01ABC...",
      "name": "document.pdf",
      "size": 204800,
      "lastModified": "2025-03-01T10:00:00Z",
      "webUrl": "https://spglobal-my.sharepoint.com/...",
      "mimeType": "application/pdf",
      "driveItemId": "01ABC..."
    }
  ]
}

Graph API call:
  GET https://graph.microsoft.com/v1.0/me/drive/items/{folderId}/children
  Filter: only return items where folder property is null (i.e. files only)
  Select: id, name, size, lastModifiedDateTime, webUrl, file

The selected file objects are stored in sparkFiles[] state.
When you provide the Spark document upload endpoint,
pass sparkFiles to handleSubmit and call the upload API there.
──────────────────────────────────────────────────────────────
*/
